import React, { useState, useEffect } from 'react';
import AiEvaluationComponent from '../components/AiEvaluation';
import AiAdviceList from '../components/AiAdviceList';
import MtgCheckList from '../components/MtgCheckList';
import TodoList from '../components/TodoList';
import DocumentButtons from '../components/DocumentButtons';
import SettingsPage from '../components/SettingsPage';
import AiManagerSelectionPage from '../components/AiManagerSelectionPage';
import MasterPage from '../components/MasterPage';
import AiHistoryPage from '../components/AiHistoryPage';
import { useMeeting } from '../hooks/useMeeting';
import type { AiManager, MeetingCheckTemplate, MeetingGoal } from '../types/meeting';

// 初期データ（実際の運用ではAPIから取得）
interface MeetingPageProps {
  recordingId: string;
  apiToken: string;
  aiManagers: AiManager[];
  templates: MeetingCheckTemplate[];
  meetingGoals: MeetingGoal[];
  companyId?: string;
  companyName?: string;
}

const MeetingPage: React.FC<MeetingPageProps> = ({
  recordingId,
  apiToken,
  aiManagers: initialAiManagers,
  templates: initialTemplates,
  meetingGoals: initialMeetingGoals,
  companyId,
  companyName,
}) => {
  const {
    meetingName,
    setMeetingName,
    scheduledDurationMinutes,
    setScheduledDurationMinutes,
    customGoalText,
    setCustomGoalText,
    selectedTemplateId,
    setSelectedTemplateId,
    aiManagers,
    aiAdviceHistory,
    mtgChecks,
    nextActions,
    aiEvaluation,
    templates,
    toggleAiManager,
    startPolling,
    generateAndDownloadMinutes,
    generateMinutesOnly,
    getCurrentGoal,
  } = useMeeting({
    recordingId,
    apiToken,
    aiManagers: initialAiManagers,
    templates: initialTemplates,
    meetingGoals: initialMeetingGoals,
  });

  // UI状態
  const [isAiEvaluationExpanded, setIsAiEvaluationExpanded] = useState(true);
  const [isSettingsOpen, setIsSettingsOpen] = useState(true); // 初期表示で設定ページを開く
  const [isAiManagerSelectionOpen, setIsAiManagerSelectionOpen] = useState(false);
  const [isMasterPageOpen, setIsMasterPageOpen] = useState(false);
  const [isHistoryPageOpen, setIsHistoryPageOpen] = useState(false);
  const [settingsReturnPage, setSettingsReturnPage] = useState<string | null>(null);

  // 選択されたテンプレート名
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  // C#から呼び出し可能な関数をwindowに公開
  // Fire-and-Forget パターン: リクエスト送信を開始し、即座に応答を返す
  // C#側は5秒待機してからプロセスを終了する
  useEffect(() => {
    (window as any).generateMinutesForCSharp = (): Promise<{ success: boolean }> => {
      return new Promise((resolve) => {
        // 議事録生成リクエストを非同期で送信開始
        generateMinutesOnly()
          .then((result) => {
            console.log('generateMinutesForCSharp: completed', result);
          })
          .catch((error) => {
            console.error('generateMinutesForCSharp: failed', error);
          });
        
        // リクエスト送信開始後、即座に成功を返す（レスポンスは待たない）
        // 少し遅延を入れてfetchが確実に開始されるようにする
        setTimeout(() => {
          resolve({ success: true });
        }, 100);
      });
    };
    return () => {
      delete (window as any).generateMinutesForCSharp;
    };
  }, [generateMinutesOnly]);

  // 設定ページを開く
  const openSettings = () => {
    setIsSettingsOpen(true);
    setIsHistoryPageOpen(false);
    setIsMasterPageOpen(false);
    setIsAiManagerSelectionOpen(false);
  };

  // 設定ページを閉じる
  const closeSettings = () => {
    setIsSettingsOpen(false);
  };

  // AIマネージャー選択ページを開く（設定から）
  const showAiManagerSelectionFromSettings = () => {
    setSettingsReturnPage('settings');
    setIsSettingsOpen(false);
    setIsAiManagerSelectionOpen(true);
  };

  // AIマネージャー選択ページを開く（履歴から）
  const showAiManagerSelectionFromHistory = () => {
    setSettingsReturnPage('history');
    setIsHistoryPageOpen(false);
    setIsAiManagerSelectionOpen(true);
  };

  // AIマネージャー選択ページを閉じる
  const closeAiManagerSelection = () => {
    setIsAiManagerSelectionOpen(false);
    if (settingsReturnPage === 'settings') {
      setIsSettingsOpen(true);
    } else if (settingsReturnPage === 'history') {
      setIsHistoryPageOpen(true);
    }
    setSettingsReturnPage(null);
  };

  // マスタページを開く（設定から）
  const showMasterPageFromSettings = () => {
    setSettingsReturnPage('settings');
    setIsSettingsOpen(false);
    setIsMasterPageOpen(true);
  };

  // マスタページを開く（メインから）
  const showMasterPage = () => {
    setSettingsReturnPage(null);
    setIsMasterPageOpen(true);
  };

  // マスタページを閉じる
  const closeMasterPage = () => {
    setIsMasterPageOpen(false);
    if (settingsReturnPage === 'settings') {
      setIsSettingsOpen(true);
    }
    setSettingsReturnPage(null);
  };

  // 履歴ページを開く
  const showHistory = () => {
    setIsHistoryPageOpen(true);
    setIsSettingsOpen(false);
    setIsMasterPageOpen(false);
    setIsAiManagerSelectionOpen(false);
  };

  // 履歴ページを閉じる
  const closeHistory = () => {
    setIsHistoryPageOpen(false);
  };

  // AIアシスタント開始
  const handleStartAiAssistant = () => {
    startPolling();
    closeSettings();
  };

  // テンプレート選択
  const handleSelectTemplate = (templateId: number) => {
    setSelectedTemplateId(templateId);
    closeMasterPage();
  };

  return (
    <div className="overlay-container">
      <div className="side-panel">
        {/* メインコンテンツ */}
        <div className={`main-content ${isSettingsOpen || isHistoryPageOpen || isMasterPageOpen || isAiManagerSelectionOpen ? 'hidden' : ''}`}>
          {/* ヘッダー */}
          <div className="panel-header">
            <div className="header-info">
              <div className="header-title" id="meeting-name-header">
                {meetingName || '新規ミーティング'}
              </div>
              <div className="header-goal" id="meeting-goal-header">
                {getCurrentGoal() || '目的・ゴール未設定'}
              </div>
            </div>
            <div className="settings-icon material-symbols-outlined" onClick={openSettings}>
              settings
            </div>
          </div>

          {/* スクロール可能な中央エリア */}
          <div className="scrollable-content">
            <AiEvaluationComponent
              evaluation={aiEvaluation}
              isExpanded={isAiEvaluationExpanded}
              onToggle={() => setIsAiEvaluationExpanded(!isAiEvaluationExpanded)}
            />

            <AiAdviceList
              aiAdviceHistory={aiAdviceHistory}
              aiManagers={aiManagers}
              onShowHistory={showHistory}
            />

            <MtgCheckList
              mtgChecks={mtgChecks}
              templateName={selectedTemplate?.template_name}
              onShowMasterPage={showMasterPage}
            />

            <TodoList nextActions={nextActions} />

            <DocumentButtons onGenerateMinutes={generateAndDownloadMinutes} />
          </div>
        </div>

        {/* 設定ページ */}
        <SettingsPage
          isOpen={isSettingsOpen}
          onClose={closeSettings}
          meetingName={meetingName}
          onMeetingNameChange={setMeetingName}
          scheduledDurationMinutes={scheduledDurationMinutes}
          onScheduledDurationChange={setScheduledDurationMinutes}
          customGoalText={customGoalText}
          onCustomGoalChange={setCustomGoalText}
          aiManagers={aiManagers}
          selectedTemplateId={selectedTemplateId}
          templates={templates}
          onShowAiManagerSelection={showAiManagerSelectionFromSettings}
          onShowMasterPage={showMasterPageFromSettings}
          onStartAiAssistant={handleStartAiAssistant}
          companyId={companyId}
          companyName={companyName}
        />

        {/* AIマネージャー選択ページ */}
        <AiManagerSelectionPage
          isOpen={isAiManagerSelectionOpen}
          onClose={closeAiManagerSelection}
          aiManagers={aiManagers}
          onToggleManager={toggleAiManager}
        />

        {/* マスタページ */}
        <MasterPage
          isOpen={isMasterPageOpen}
          onClose={closeMasterPage}
          templates={templates}
          selectedTemplateId={selectedTemplateId}
          onSelectTemplate={handleSelectTemplate}
        />

        {/* 履歴ページ */}
        <AiHistoryPage
          isOpen={isHistoryPageOpen}
          onClose={closeHistory}
          aiAdviceHistory={aiAdviceHistory}
          aiManagers={aiManagers}
          onShowAiManagerSelection={showAiManagerSelectionFromHistory}
        />
      </div>
    </div>
  );
};

export default MeetingPage;
